'use client';

import { type ReactNode, useEffect, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import FaIcon, { faXmark } from '../../icon/FaIcon/FaIcon';
import styles from './ModalBase.module.scss';

type Props = {
  isOpen: boolean;
  onClose: VoidFunction;
  children?: ReactNode;
  textColor?: string;
  bgColor?: 'white' | 'gray100' | 'gray200' | 'transparent';
  showDefaultCloseButton?: boolean;
  showDefaultCloseButtonOnlyOnPc?: boolean;
  showBottomCloseButton?: boolean;
  maskBgColor?: string;
  closeButtonBgColor?: string;
  modalContainerSize?: 'default' | 'xSmall' | 'small' | 'mediumLarge' | 'large' | 'xLarge';
  isBackgroundNoScroll?: boolean;
  closeOnBackgroundClick?: boolean;
};

function ModalBase({
  isOpen,
  onClose,
  children,
  textColor = 'black',
  bgColor = 'white',
  showDefaultCloseButton = true,
  showDefaultCloseButtonOnlyOnPc = false,
  showBottomCloseButton = false,
  maskBgColor = 'black',
  closeButtonBgColor = 'gray100',
  modalContainerSize = 'default',
  closeOnBackgroundClick = false,
}: Props) {
  // モーダル開いてる時は背景スクロールさせない
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'auto';
      };
    }
  }, [isOpen]);

  const generateStyle = ({ textColor, bgColor }: Pick<Props, 'textColor' | 'bgColor'>): React.CSSProperties => {
    if (bgColor === 'transparent') {
      return {
        backgroundColor: 'transparent',
      };
    }
    return {
      backgroundColor: `var(--color-${bgColor})`,
      color: `var(--color-${textColor})`,
    };
  };

  const generateMaskStyle = ({ maskBgColor }: Pick<Props, 'maskBgColor'>): React.CSSProperties => {
    return {
      backgroundColor: maskBgColor,
    };
  };

  const generateCloseButtonStyle = ({ closeButtonBgColor }: Pick<Props, 'closeButtonBgColor'>): React.CSSProperties => {
    if (closeButtonBgColor === 'gray100') {
      return {
        backgroundColor: `var(--color-${closeButtonBgColor})`,
        padding: '0px',
      };
    }
    return {
      backgroundColor: closeButtonBgColor,
    };
  };

  // warning対策
  const nodeRef = useRef(null);

  // 背景クリック時のハンドラ
  const handleBackgroundClick = () => {
    if (closeOnBackgroundClick) {
      onClose();
    }
  };

  // モーダルコンテナ内のクリックで閉じないようにする
  const handleModalContainerClick = (e: React.MouseEvent<HTMLElement>) => {
    // 透過背景で背景クリックで閉じる設定の場合、イベント伝播を止めない
    if (bgColor === 'transparent' && closeOnBackgroundClick) {
      return;
    }
    e.stopPropagation();
  };

  return (
    <CSSTransition in={isOpen} timeout={300} unmountOnExit classNames={styles} nodeRef={nodeRef}>
      <div ref={nodeRef} className={styles.modalWrapper}>
        <div className={styles.modalMask} style={generateMaskStyle({ maskBgColor })} />
        <section className={`${styles.fixedModalContainer}`} onClick={handleBackgroundClick}>
          <div className={`${styles.modalContainer} ${styles[`${modalContainerSize}ModalContainer`]}`}>
            <div
              className={`${styles.modalBody} ${showBottomCloseButton ? styles.defaultHeightModalBody : styles.largeHeightModalBody}`}
              style={generateStyle({ textColor, bgColor })}
              onClick={handleModalContainerClick}
            >
              {showDefaultCloseButton && (
                <div className={styles.modalHeader}>
                  <button
                    type='button'
                    className={`${styles.modalClose} ${showDefaultCloseButtonOnlyOnPc ? styles.hideOnSp : ''}`}
                    onClick={() => onClose()}
                    style={generateCloseButtonStyle({ closeButtonBgColor })}
                  >
                    <FaIcon icon={faXmark} />
                  </button>
                </div>
              )}
              {children}
            </div>
            {showBottomCloseButton && (
              <button type='button' className={styles.modalFooterClose} onClick={() => onClose()}>
                閉じる
              </button>
            )}
          </div>
        </section>
      </div>
    </CSSTransition>
  );
}

export default ModalBase;
